/**
 * The fields that define the document a SPLIT-OFF occurrence becomes.
 *
 * Dragging or editing one occurrence out of a recurring series creates a new
 * document beside the original. Exactly four things about that document decide
 * whether the customer's message survives the operation: where it is due, what
 * rule (if any) it carries, whether it is recurring, and — the one that made
 * messages vanish — which create-dedup token it is minted with.
 *
 * This lives in its own pure function for one reason, and it is not tidiness.
 * The test harness used to RE-IMPLEMENT this construction rather than call it,
 * and in doing so hardcoded the very fix it was meant to be guarding. Its
 * `swallowedCreates === 0` assertions therefore passed against a product with
 * the fix removed: the suite was testing itself. A recurrence suite that cannot
 * fail is worse than no suite, because it manufactures the confidence that let
 * this area be declared fixed four times while it stayed broken.
 *
 * So the rule is: the calendar thunk and the harness must both build the
 * replacement HERE. Break this function and both go red together.
 */
import { deriveSplitOccurrenceIdempotencyKey } from './split-occurrence-idempotency-key';

export interface SplitOccurrencePlan {
  /** Where the handler decided the split-off occurrence now falls. */
  newDueDate?: string | Date | null;
  /** A rule for the new document ("this and following"), or absent for a single occurrence. */
  newRRule?: string;
}

export interface SplitOccurrenceFields<TDue = string | Date | null | undefined> {
  dueDate: TDue;
  rruleset: string | undefined;
  isRecurring: boolean;
  clientIdempotencyKey: string | undefined;
  /** The dates here are already resolved instants; the day-mapping fix must not re-map them. */
  skipDayMappingFix: true;
}

/**
 * Build the replacement document's defining fields.
 *
 * @param plan       what the recurrence handler decided.
 * @param parentKey  the ORIGINAL document's `clientIdempotencyKey`, if it has one.
 * @param occurrence the SOURCE occurrence being split off — the key is derived from
 *                   this, not from the destination, so that two different occurrences
 *                   dropped on the same day stay two different messages.
 * @param toDueDate  how the caller wants the due date represented (the thunk stores a
 *                   different shape than the harness); defaults to passing it through.
 */
export function buildSplitOccurrenceFields<TDue = string | Date | null | undefined>(
  plan: SplitOccurrencePlan,
  parentKey: string | undefined | null,
  occurrence: Date | string | number | undefined | null,
  toDueDate: (d: string | Date | null | undefined) => TDue = (d => d) as (d: string | Date | null | undefined) => TDue,
): SplitOccurrenceFields<TDue> {
  return {
    dueDate: toDueDate(plan.newDueDate),
    rruleset: plan.newRRule,
    isRecurring: !!plan.newRRule,
    // A new document needs its own create-dedup token. Inheriting the parent's made
    // the backend's `dedupeByClientIdempotencyKey` hand back the parent and insert
    // nothing — silently, with a 201 — so the occurrence left the series via its
    // EXDATE and arrived nowhere.
    // Keyed on BOTH ends of the move: the source keeps two occurrences dropped on one
    // day apart, and the destination stops a retry that changes its mind from being
    // deduped onto whatever a previous, partly-failed attempt already created.
    clientIdempotencyKey: deriveSplitOccurrenceIdempotencyKey(parentKey, occurrence, plan.newDueDate),
    skipDayMappingFix: true,
  };
}
